<script context="module" lang="ts">
  // https://www.figma.com/file/7Tb4JFmyloSdGQHCc8r2t8/Insights-Components-2023?node-id=1%3A688&mode=dev
  /**
   * if you need to override color, use style prop syntax
   * https://svelte.dev/repl/f1f974410a9e4fa5a408ab8c87baa6ae?version=3.46.1
   */
  export const TEXT_SIZES = ["xxs", "xs", "s", "m", "l", "xl"] as const;
  export const TEXT_FONTS = ["rekki", "ocr", "rekkimedium", "iawriter"] as const;
  export const TEXT_WEIGHTS = ["regular", "medium"] as const;

  export type TextSize = (typeof TEXT_SIZES)[number];

  // rekkimedium is for cases where we want to use the medium fontface without a weight of 500
  export type TextFont = (typeof TEXT_FONTS)[number];
  export type TextWeight = (typeof TEXT_WEIGHTS)[number];

  export type TextOverflow = "ellipsis" | "initial" | "lineClamp";
</script>

<script lang="ts">
  export let size: TextSize = "m";
  export let as = "p";
  export let weight: TextWeight = "regular";
  export let font: TextFont = "rekki";
  export let tabular = false;
  export let overflow: TextOverflow = "initial";
</script>

<svelte:element
  this={as}
  class="base {font === 'ocr' ? `ocr-${size}` : size} {weight}"
  class:font={font === "rekki"}
  class:font-ocr={font === "ocr"}
  class:tabular={tabular === true}
  class:ellipsis={overflow === "ellipsis"}
  class:rekkimedium={font === "rekkimedium"}
  class:line-clamp={overflow === "lineClamp"}
>
  <slot />
</svelte:element>

<style>
  .base {
    color: var(--rk-text-color);
    display: var(--text-display, revert);
    white-space: var(--whitespace, revert);
    overflow: var(--overflow, revert);
  }

  .line-clamp {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--line-clamp, 3);
  }

  .rekkimedium {
    font-family: "DiatypeRekkiMedium";
  }

  .ellipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .font-ocr {
    font-family: "OCDRekki";
    text-transform: uppercase;
  }
  .tabular {
    font-family: "DiatypeRekkiTabular";
    font-variant-numeric: slashed-zero;
  }
  .font {
    font-family: "DiatypeRekki";
  }

  .font-iawriter {
    font-family: "iA Writer Mono S";
  }

  .ocr-xl {
    font-size: var(--rk-font-size-ocr-xl);
    line-height: var(--rk-line-height-ocr-xl);
  }
  .ocr-l {
    font-size: var(--rk-font-size-ocr-l);
    line-height: var(--rk-line-height-ocr-l);
  }
  .ocr-m {
    font-size: var(--rk-font-size-ocr-m);
    line-height: var(--rk-line-height-ocr-m);
  }
  .ocr-s {
    font-size: var(--rk-font-size-ocr-s);
    line-height: var(--rk-line-height-ocr-s);
  }

  .ocr-xs {
    font-size: var(--rk-font-size-ocr-xs);
    line-height: var(--rk-line-height-ocr-xs);
  }

  .xl {
    font-size: var(--rk-font-size-xl);
    line-height: var(--rk-line-height-xl);
    letter-spacing: -1px;
  }
  .l {
    font-size: var(--rk-font-size-l);
    line-height: var(--rk-line-height-l);
    letter-spacing: -1px;
  }
  .m {
    font-size: var(--rk-font-size-m);
    line-height: var(--rk-line-height-m);
    letter-spacing: -0.5px;
  }
  .s {
    font-size: var(--size, var(--rk-font-size-s));
    line-height: var(--line-height, var(--rk-line-height-s));
    letter-spacing: -0.3px;
  }
  .xs {
    font-size: var(--size, var(--rk-font-size-xs));
    line-height: var(--line-height, var(--rk-line-height-xs));
    letter-spacing: -0.005em;
  }
  .xxs {
    font-size: var(--rk-font-size-xxs);
    line-height: var(--rk-line-height-xxs);
    letter-spacing: -0.001em;
  }
  .medium {
    font-weight: 500;
  }
  .regular {
    font-weight: 400;
  }

  .dark {
    color: var(--rekki-dark);
  }

  .light {
    color: var(--rekki-light);
  }

  .neutral-01 {
    color: var(--rekki-neutral-01);
  }

  .neutral-02 {
    color: var(--rekki-neutral-02);
  }

  .neutral-03 {
    color: var(--rekki-neutral-03);
  }

  .neutral-04 {
    color: var(--rekki-neutral-04);
  }

  .transparency-dark {
    color: var(--rekki-transparency-dark);
  }

  .transparency-light {
    color: var(--rekki-transparency-light);
  }
</style>
